// Resource invalidations are shared by local reads, MQTT and reconciliation.
export const terminalRefreshActions = {
  shop: ['fetchShopList'],
  admin: ['fetchAdminList'],
  air: ['fetchAirList'],
  planeClass: ['fetchPlaneClassList', 'fetchAirList'],
  planeModel: ['fetchPlaneModelList'],
  site: ['fetchSiteList'],
  route: ['fetchRouteList'],
  category: ['fetchCategoryList'],
  banner: ['fetchBannerList'],
  notice: ['fetchNoticeList'],
  spu: ['fetchSpuList'],
  sku: ['fetchSkuList'],
  paidOrder: ['fetchPaidOrderList'],
  message: ['fetchMessageList'],
  mapStyle: ['fetchMapStyleList'],
  nofly: ['fetchNoflyData']
}

export function createTerminalDataSync () {
  let session = null
  const resources = new Map()
  function resource (action, token) {
    if (token !== session) {
      resources.clear()
      session = token
    }
    if (!resources.has(action)) resources.set(action, { epoch: 0, applied: -1, pending: null })
    return resources.get(action)
  }
  return {
    invalidate (keys, token) {
      const actions = new Set(keys.flatMap(key => Object.prototype.hasOwnProperty.call(terminalRefreshActions, key) ? terminalRefreshActions[key] : []))
      actions.forEach(action => { resource(action, token).epoch += 1 })
    },
    needsRefresh (action, token) {
      const item = resource(action, token)
      return item.applied !== item.epoch
    },
    wrap (action, read) {
      return async (context, payload) => {
        const token = context.rootState.user.token
        const item = resource(action, token)
        const epoch = item.epoch
        if (item.pending) {
          const pending = item.pending
          // A pre-notification read cannot satisfy an invalidation received in flight.
          try { await pending.promise } catch (error) {
            if (pending.epoch === epoch) throw error
          }
          if (context.rootState.user.token !== token) return
          if (pending.epoch === item.epoch && item.applied === item.epoch) return pending.value
          return this.wrap(action, read)(context, payload)
        }
        const current = () => context.rootState.user.token === token && item.epoch === epoch
        const guardedContext = {
          ...context,
          isDataSyncCurrent: current,
          commit: (...args) => { if (current()) context.commit(...args) }
        }
        const pending = { epoch }
        item.pending = pending
        pending.promise = Promise.resolve().then(() => read(guardedContext, payload))
        try {
          pending.value = await pending.promise
          if (current()) item.applied = epoch
          return pending.value
        } finally {
          if (item.pending === pending) item.pending = null
        }
      }
    }
  }
}

export const terminalDataSync = createTerminalDataSync()

export function withTerminalDataSync (options) {
  new Set(Object.values(terminalRefreshActions).flat()).forEach(action => {
    if (options.actions[action]) options.actions[action] = terminalDataSync.wrap(action, options.actions[action])
  })
  // Keep unchanged rows and arrays stable instead of rebuilding the UI on every poll.
  const listStates = ['shop', 'admin', 'air', 'planeClass', 'planeModel', 'site', 'route', 'category', 'banner', 'notice', 'spu', 'sku', 'paidOrder', 'message', 'mapStyle']
  listStates.forEach(key => {
    const stateKey = key + 'List'
    const name = 'set' + stateKey[0].toUpperCase() + stateKey.slice(1)
    const mutation = options.mutations[name]
    if (!mutation) return
    options.mutations[name] = (state, list) => {
      const previous = state[stateKey]
      if (Array.isArray(previous) && Array.isArray(list)) {
        const identity = (row, index) => String(row.shop_id || '') + ':' + (row.id == null ? index : row.id)
        const rows = new Map(previous.map((row, index) => [identity(row, index), row]))
        list = list.map((row, index) => {
          const old = rows.get(identity(row, index))
          const snapshot = value => JSON.stringify(value, (key, item) => key === 'planeState' ? undefined : item)
          return old && old.planeState === row.planeState && snapshot(old) === snapshot(row) ? old : row
        })
        if (previous.length === list.length && previous.every((row, i) => row === list[i])) list = previous
      }
      mutation(state, list)
    }
  })
  return options
}
